import { Button, Checkbox, Divider, FormLabel, Group, IconCrossCircle, Input, Select } from '@screentone/core';

import { ParametersType, useGetModuleParameterSchemasQuery } from 'data/generated/graphql';
import { useConvertedProperty } from 'hooks';

interface Props {
  moduleParameters: ParametersType[];
  onChange: (updated: ParametersType[]) => void;
  disabled?: boolean;
}

const getValue = (params: ParametersType[], key: string): string => params.find((p) => p.name === key)?.value ?? '';

const setValue = (params: ParametersType[], key: string, value: string): ParametersType[] => {
  const existing = params.find((p) => p.name === key);
  if (existing) {
    return params.map((p) => (p.name === key ? { ...p, value } : p));
  }
  return [...params, { name: key, value, type: 'parameter' }];
};

export const ModuleParameters = ({ moduleParameters, onChange, disabled = false }: Props) => {
  const currentProperty = useConvertedProperty();

  const { data, isLoading } = useGetModuleParameterSchemasQuery(
    { publicationKey: currentProperty ?? '' },
    { enabled: !!currentProperty }
  );

  const schemas = data?.getModuleParameterSchemas ?? [];

  if (isLoading || schemas.length === 0) return null;

  const handleChange = (key: string, value: string) => {
    onChange(setValue(moduleParameters, key, value));
  };

  return (
    <>
      <Divider margin={{ top: 'md', bottom: 'md' }} />
      <Group direction="column" gap="md">
        {schemas.map((schema) => {
          const currentValue = getValue(moduleParameters, schema.key);
          const isDeprecated = schema.deprecated === true;
          const labelWithDeprecated = isDeprecated ? `${schema.label} (deprecated)` : schema.label;

          if (schema.type === 'boolean') {
            return (
              <FormLabel key={schema.key} label={labelWithDeprecated} labelPosition="right" fullWidth>
                <Checkbox
                  checked={currentValue === 'true'}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleChange(schema.key, e.target.checked ? 'true' : 'false')
                  }
                  disabled={isDeprecated || disabled}
                />
              </FormLabel>
            );
          }

          if (schema.type === 'enum' && schema.enumValues?.length) {
            return (
              <FormLabel key={schema.key} label={labelWithDeprecated} fullWidth>
                <Select
                  value={currentValue}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleChange(schema.key, e.target.value)}
                  disabled={isDeprecated || disabled}
                >
                  <option value="">— Select —</option>
                  {schema.enumValues.map((val) => (
                    <option key={val} value={val}>
                      {val}
                    </option>
                  ))}
                </Select>
              </FormLabel>
            );
          }

          return (
            <FormLabel key={schema.key} label={labelWithDeprecated} fullWidth>
              <Group gap="xs" wrap={false} valign="center">
                <Input
                  value={currentValue}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleChange(schema.key, e.target.value)}
                  placeholder={schema.label}
                  disabled={isDeprecated || disabled}
                />
                {!isDeprecated && (
                  <Button
                    tertiary
                    icon={IconCrossCircle}
                    onClick={() => handleChange(schema.key, '')}
                    disabled={disabled}
                  />
                )}
              </Group>
            </FormLabel>
          );
        })}
      </Group>
    </>
  );
};
